import { groupCitations } from '@/services/hmRagContent'

const blockTags = [
  'ARTICLE', 'BLOCKQUOTE', 'DD', 'DIV', 'DL', 'DT', 'H1', 'H2', 'H3', 'H4',
  'HR', 'LI', 'OL', 'P', 'PRE', 'SECTION', 'TABLE', 'TBODY', 'THEAD', 'TR', 'UL'
]
const escapes = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }

function escapeHtml(value) {
  return String(value === null || value === undefined ? '' : value)
    .replace(/[&<>"]/g, character => escapes[character])
}

function appendText(node, buffer) {
  Array.prototype.slice.call(node.childNodes).forEach(child => {
    if (child.nodeType === 3) {
      buffer.push((child.nodeValue || '').replace(/\s+/g, ' '))
      return
    }
    if (child.nodeType !== 1) return
    if (child.tagName === 'BR') {
      buffer.push('\n')
      return
    }
    const isBlock = blockTags.indexOf(child.tagName) !== -1
    if (isBlock) buffer.push('\n')
    appendText(child, buffer)
    if (isBlock) buffer.push('\n')
  })
}

// A pasted answer has to keep its paragraph breaks. innerText would give them to us, but only for
// a node the browser has laid out -- and jsdom has no innerText at all -- so walk the markup and
// turn every block element into a line break of its own.
export function answerPlainText(answerHtml) {
  const container = document.createElement('div')
  container.innerHTML = answerHtml || ''
  const buffer = []
  appendText(container, buffer)
  return buffer.join('')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// What the Copy button puts on the clipboard. The [n] markers copy as bare text -- the popover
// they open is a page affordance a pasted answer cannot carry -- while the cited document keeps
// its link in the rich flavour and gets its URL spelled out in the plain one.
export function buildAnswerClipboardPayload(answerHtml, citations) {
  const container = document.createElement('div')
  container.innerHTML = answerHtml || ''
  Array.prototype.slice.call(container.querySelectorAll('a.evi')).forEach(anchor => {
    anchor.parentNode.replaceChild(document.createTextNode(anchor.textContent), anchor)
  })

  let html = container.innerHTML
  let text = answerPlainText(html)
  const groups = groupCitations(citations)

  if (groups.length) {
    html += '<p>Sources:<br>' + groups.map(group => {
      const tag = `[${group.numbers.join(', ')}] `
      const tail = group.docType ? ` (${escapeHtml(group.docType)})` : ''
      const label = escapeHtml(group.label)
      return group.url
        ? `${tag}<a href="${encodeURI(group.url)}">${label}</a>${tail}`
        : tag + label + tail
    }).join('<br>') + '</p>'

    text += '\n\nSources:\n' + groups.map(group => {
      const tail = group.docType ? ` (${group.docType})` : ''
      const url = group.url ? `\n    ${group.url}` : ''
      return `[${group.numbers.join(', ')}] ${group.label}${tail}${url}`
    }).join('\n')
  }

  return { html, text }
}

// The button reports the outcome in its own label and reverts a moment later, so the answer is
// never covered by a toast or a dialog.
export function copyButtonLabel(copyState) {
  if (copyState === 'ok') return '✓ Copied'
  if (copyState === 'failed') return 'Copy failed'
  return 'Copy'
}

export async function writeClipboard(html, text) {
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' })
      })])
      return true
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (error) {
    // fall through to the hidden-selection copy below
  }

  // navigator.clipboard is undefined outside a secure context -- an intranet http host such as
  // http://hmdev03:83 is exactly that -- so fall back to copying a hidden selection.
  try {
    const holder = document.createElement('div')
    holder.contentEditable = 'true'
    holder.innerHTML = html
    holder.style.cssText = 'position:fixed;left:-9999px;top:0'
    document.body.appendChild(holder)
    const range = document.createRange()
    range.selectNodeContents(holder)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    const copied = document.execCommand('copy')
    selection.removeAllRanges()
    document.body.removeChild(holder)
    return copied
  } catch (error) {
    return false
  }
}
